// Cover art URL helper.
//
// Hosts we serve covers from (image.simplecastcdn.com, cdn.podgo.io, Supabase
// storage) ignore transform params and hand back the full-size original — on
// the homepage that meant ~9 MB of 1–2 MB artwork rendered into ~140px boxes.
// Cloudflare Image Resizing is not enabled on this zone, so remote covers are
// routed through wsrv.nl, a free image-resizing/CDN proxy, which returns a
// correctly sized WebP. megaphone.imgix.net resizes natively, so it keeps
// using its own params rather than adding a proxy hop.

const IMGIX_HOSTS = new Set(["megaphone.imgix.net"]);
const PROXY_HOST = "wsrv.nl";
const PROXY = `https://${PROXY_HOST}/`;

/** Proxy only absolute http(s) URLs on a real remote host. */
function isProxyable(u: URL): boolean {
  if (u.protocol !== "http:" && u.protocol !== "https:") return false;
  if (u.hostname === PROXY_HOST) return false;
  return !["localhost", "127.0.0.1"].includes(u.hostname);
}


export function coverSrc(url?: string | null, size = 224, dpr = 1): string {
  if (!url) return "";
  try {
    const u = new URL(url);

    if (IMGIX_HOSTS.has(u.hostname)) {
      u.searchParams.set("w", String(size));
      u.searchParams.set("h", String(size));
      u.searchParams.set("fit", "crop");
      u.searchParams.set("auto", "format,compress");
      u.searchParams.set("q", "72");
      if (dpr > 1) u.searchParams.set("dpr", String(dpr));
      return u.toString();
    }

    if (!isProxyable(u)) return url;

    const width = Math.round(size * (dpr > 1 ? dpr : 1));
    const params = new URLSearchParams({
      url: u.toString(),
      w: String(width),
      h: String(width),
      fit: "cover",
      output: "webp",
      q: "72",
      // Serve the original untouched if the proxy can't process it, rather
      // than showing a broken image.
      default: u.toString(),
    });
    return `${PROXY}?${params.toString()}`;
  } catch {
    return url;
  }
}

/**
 * Responsive srcSet for a cover. Every remote host is resizable now, so this
 * emits real width descriptors instead of duplicate URLs.
 */
export function coverSrcSet(url: string | null | undefined, widths: number[]): string | undefined {
  if (!url) return undefined;
  const base = coverSrc(url, widths[0]);
  if (base === url) return undefined;
  return widths.map((w) => `${coverSrc(url, w)} ${w}w`).join(", ");
}

/**
 * Non-square artwork (press/publication logos). Unlike `coverSrc` this keeps
 * the original aspect ratio (fit=contain, no crop) and preserves transparency
 * by staying in WebP with an alpha-safe background.
 */
export function logoSrc(url?: string | null, width = 160): string {
  if (!url) return "";
  try {
    const u = new URL(url);
    if (!isProxyable(u)) return url;
    const params = new URLSearchParams({
      url: u.toString(),
      w: String(width),
      fit: "inside",
      we: "", // never upscale past the original
      output: "webp",
      q: "80",
      default: u.toString(),
    });
    return `${PROXY}?${params.toString()}`;
  } catch {
    return url;
  }
}

/** Responsive set for `logoSrc`; undefined when the host can't be resized. */
export function logoSrcSet(url: string | null | undefined, widths: number[]): string | undefined {
  if (!url) return undefined;
  if (logoSrc(url, widths[0]) === url) return undefined;
  return widths.map((w) => `${logoSrc(url, w)} ${w}w`).join(", ");
}
